import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useTrashedPatientCategories,
  useRestorePatientCategories,
  usePermanentlyDeletePatientCategories,
} from '../../services/httpServices/queries';
import type { PatientCategory } from '../../services/httpServices/patientCategoryService';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { AlertDialog } from '../../components/ui/AlertDialog';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  RotateCcw,
  Trash2,
  ArrowLeft,
  Tags,
} from 'lucide-react';

const LIMIT = 20;

export function PatientCategoriesTrash() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<
    { mode: 'single'; category: PatientCategory } | { mode: 'bulk' } | null
  >(null);
  const [blockedDialog, setBlockedDialog] = useState<{ title: string; messages: string[] } | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useTrashedPatientCategories(
    page,
    LIMIT,
    debouncedSearch.trim() || undefined,
  );
  const restoreCategories = useRestorePatientCategories();
  const permanentDelete = usePermanentlyDeletePatientCategories();

  const categories = data?.data ?? [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  const categoryName = (category: PatientCategory) => category?.label || category?.name || 'Unknown';

  const allSelected = categories.length > 0 && categories.every((c) => selectedIds.includes(c.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !categories.some((c) => c.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...categories.map((c) => c.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleRestore = async (category: PatientCategory) => {
    try {
      const res = await restoreCategories.mutateAsync([category.id]);
      const blocked = res?.data?.blocked ?? [];
      if (blocked.length > 0) {
        toast.error(blocked[0]?.reason || 'This category could not be restored');
        return;
      }
      setSelectedIds((prev) => prev.filter((x) => x !== category.id));
      toast.success(`"${categoryName(category)}" restored`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore category');
    }
  };

  const handleBulkRestore = async () => {
    const ids = [...selectedIds];
    try {
      const res = await restoreCategories.mutateAsync(ids);
      const restored = res?.data?.restored?.length ?? 0;
      const blocked = res?.data?.blocked ?? [];
      if (restored > 0) {
        toast.success(`Restored ${restored} categor${restored === 1 ? 'y' : 'ies'}`);
      }
      if (blocked.length > 0) {
        setBlockedDialog({
          title: `Could not restore ${blocked.length} categor${blocked.length === 1 ? 'y' : 'ies'}`,
          messages: blocked.map((b) => b.reason),
        });
      } else if (restored === 0) {
        toast.error('No categories were restored');
      }
      setSelectedIds([]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore categories');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        const res = await permanentDelete.mutateAsync([confirmTarget.category.id]);
        const blocked = res?.data?.blocked ?? [];
        if (blocked.length > 0) {
          setBlockedDialog({
            title: 'Cannot delete permanently',
            messages: [
              `${categoryName(confirmTarget.category)} — ${blocked[0]?.reason ?? 'This category could not be permanently deleted.'}`,
            ],
          });
          return;
        }
        setSelectedIds((prev) => prev.filter((x) => x !== confirmTarget.category.id));
        toast.success(`"${categoryName(confirmTarget.category)}" permanently deleted`);
        if (page > 1 && categories.length === 1) setPage(page - 1);
      } else {
        const ids = [...selectedIds];
        const nameById = new Map(categories.map((c) => [c.id, categoryName(c)]));
        const res = await permanentDelete.mutateAsync(ids);
        const deleted = res?.data?.deleted?.length ?? 0;
        const blocked = res?.data?.blocked ?? [];
        if (deleted > 0) {
          toast.success(`Permanently deleted ${deleted} categor${deleted === 1 ? 'y' : 'ies'}`);
        }
        if (blocked.length > 0) {
          setBlockedDialog({
            title: `Cannot permanently delete ${blocked.length} categor${blocked.length === 1 ? 'y' : 'ies'}`,
            messages: blocked.map((b) => `${nameById.get(b.id) ?? b.id} — ${b.reason}`),
          });
        } else if (deleted === 0) {
          toast.error('No categories were deleted');
        }
        setSelectedIds([]);
        if (page > 1 && deleted === categories.length) setPage(page - 1);
      }
    } catch (err: any) {
      setBlockedDialog({
        title: 'Cannot delete permanently',
        messages: [err?.response?.data?.message || 'The categor(ies) could not be permanently deleted.'],
      });
    } finally {
      setConfirmTarget(null);
    }
  };

  const busy = restoreCategories.isPending || permanentDelete.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/patients/categories"
            className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors"
            aria-label="Back to patient categories"
          >
            <ArrowLeft size={20} />
          </Link>
          <div className="p-2 bg-danger/10 rounded-lg">
            <Trash2 className="w-5 h-5 text-danger" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text">Patient Categories Trash</h1>
            <p className="text-xs text-text-muted">Restore categories or delete them permanently</p>
          </div>
        </div>
        <Link
          to="/patients/categories"
          className="px-4 py-2 border border-border text-text-muted rounded-lg hover:text-text hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 whitespace-nowrap"
        >
          <Tags size={16} />
          Manage Categories
        </Link>
      </div>

      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search trashed categories by label, key or description..."
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {restoreCategories.isPending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Restore
            </button>
            <button
              onClick={() => setConfirmTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {permanentDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete permanently
            </button>
            <button
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed categories</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={categories.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Label</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Key</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <Tags className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">
                        {search ? 'No trashed categories match your search' : 'Trash is empty'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  categories.map((category) => (
                    <tr key={category.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(category.id)}
                          onChange={() => toggleSelectOne(category.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-text">{categoryName(category)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-text-muted">{category.name}</td>
                      <td className="px-4 py-3 text-sm text-text-muted max-w-xs truncate">
                        {category.description || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {category.deletedAt ? new Date(category.deletedAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleRestore(category)}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                            title="Restore"
                          >
                            <RotateCcw size={16} />
                          </button>
                          <button
                            onClick={() => setConfirmTarget({ mode: 'single', category })}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Delete permanently"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={meta?.page ?? page}
            totalPages={meta?.totalPages ?? 1}
            total={meta?.total ?? categories.length}
            limit={meta?.limit ?? LIMIT}
            onPageChange={(next) => {
              setPage(next);
              setSelectedIds([]);
            }}
            itemName="trashed categories"
          />
        </div>
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        title={
          confirmTarget?.mode === 'bulk'
            ? 'Delete categories permanently'
            : 'Delete category permanently'
        }
        description={
          confirmTarget?.mode === 'bulk' ? (
            <>
              Permanently delete <strong className="text-text">{selectedIds.length}</strong> selected
              categor{selectedIds.length === 1 ? 'y' : 'ies'}? This cannot be undone. Patients that still
              use these keys keep the stored key until they are reassigned.
            </>
          ) : (
            <>
              Permanently delete{' '}
              <strong className="text-text">
                {confirmTarget?.mode === 'single' ? categoryName(confirmTarget.category) : ''}
              </strong>
              ? This cannot be undone. Patients that still use this key keep the stored key until they are
              reassigned.
            </>
          )
        }
        confirmLabel="Delete permanently"
        loading={busy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />

      <AlertDialog
        open={blockedDialog !== null}
        title={blockedDialog?.title ?? 'Cannot delete permanently'}
        messages={blockedDialog?.messages}
        onClose={() => setBlockedDialog(null)}
      />
    </div>
  );
}
